import {ResourceStatus} from '@/shared/components/ui/resource-status'
import {useAppForm} from '@cypro/ui/integrations/form'
import {Avatar, Box, Select as ChakraSelect, Flex, Heading, HStack} from '@chakra-ui/react'
import {UpdateResourceStatusSchema} from '@cypro/engine/mutators/util'
import {useTranslate} from '@tolgee/react'
import {isValidElement, PropsWithChildren, ReactNode} from 'react'
import {IconType} from 'react-icons/lib'

interface Props extends PropsWithChildren {
  title: ReactNode
  description?: string | null
  icon: IconType | ReactNode
  status?: string | null
  onChangeStatus?: (status: UpdateResourceStatusSchema['status']) => void
}

export function ResourceDrawerSummary({children, title, description, icon, status, onChangeStatus}: Props) {
  const {t} = useTranslate('common')
  const form = useAppForm({
    defaultValues: {status: status ? [status] : []},
    listeners: {
      onChange({fieldApi}) {
        if (fieldApi.name === 'status') {
          const [status] = fieldApi.state.value || []
          if (status) onChangeStatus?.(status as UpdateResourceStatusSchema['status'])
        }
      },
    },
  })

  return (
    <Flex paddingY="4" alignItems="flex-start" justifyContent="space-between" width="100%">
      <HStack gapX="3" alignItems="flex-start">
        <Avatar.Root size="xl" shape="square" rounded="md">
          {icon && typeof icon === 'function' && !isValidElement(icon) && icon({size: 28})}
          {icon && isValidElement(icon) && icon}
        </Avatar.Root>
        <Box>
          <Heading as="h3" fontSize="lg" fontWeight="medium" fontFamily="body">
            {title}
          </Heading>
          {description && (
            <HStack fontSize="md" color="fg.muted" gapX="2" wordBreak="break-all">
              {description}
            </HStack>
          )}
        </Box>
      </HStack>
      {status &&
        (onChangeStatus ? (
          <form onSubmit={e => e.preventDefault()}>
            <form.AppForm>
              <form.AppField name="status">
                {field => (
                  <field.Select
                    items={[
                      {label: t('active'), value: 'active'},
                      {label: t('inactive'), value: 'inactive'},
                    ]}
                    label={t('status')}
                    hideLabel
                    contentProps={{zIndex: '99999'}}
                    positioning={{flip: true}}
                  >
                    <ResourceStatus status={status}>
                      <ChakraSelect.IndicatorGroup position="static" padding="0">
                        <ChakraSelect.Indicator />
                      </ChakraSelect.IndicatorGroup>
                    </ResourceStatus>
                  </field.Select>
                )}
              </form.AppField>
            </form.AppForm>
          </form>
        ) : (
          // read-only drawers keep the badge but must not offer a dropdown that does nothing
          <ResourceStatus status={status} />
        ))}

      {children}
    </Flex>
  )
}
